import { motion } from "framer-motion";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLayoutStore } from "../../context";
import { HomeIcon, GoalIcon, NetworkIcon, ChevronDown } from "lucide-react";
import _ from "lodash";
import constants from "../../utils/constants";

const NavItem = ({
  item,
  navigate,
  activePath,
  toggleSubmenu,
  isSubmenuOpen,
}) => {
  const isSubitemActive = item.subitems
    ? item.subitems.some((sub) => activePath === sub.path)
    : false;

  const isActive =
    item.path && item.path === constants.ROUTE_PATHS.DASHBOARD
      ? activePath === item.path
      : isSubitemActive;

  return (
    <li className="w-full">
      {/* Main Navigation Item */}
      <div
        className={`cursor-pointer rounded-md my-2 p-3 border font-semibold text-md 
          text-emerald border-cornflower hover:bg-cornflower hover:text-cream 
          ${
            isActive
              ? "bg-marigold text-cream border border-marigold hover:bg-marigold"
              : ""
          }
          ${isSubitemActive ? "bg-transparent" : ""}`}
        onClick={() => {
          if (item.subitems) {
            toggleSubmenu(item.label);
          } else if (item.path) {
            navigate(item.path);
          }
        }}
      >
        <div className="flex items-center gap-2">
          <item.icon className="h-6 w-6 me-2" />
          {item.label}
          {item.subitems && (
            <ChevronDown
              className={`h-5 w-5 transition-transform ${
                isSubmenuOpen[item.label] ? "rotate-180" : ""
              }`}
            />
          )}
        </div>
      </div>

      {/* Submenu Items */}
      {item.subitems && isSubmenuOpen[item.label] && (
        <ul className="text-emerald pl-4">
          {item.subitems.map((sub) => (
            <li
              key={sub.label}
              className={`cursor-pointer rounded-md my-2 p-2 text-md hover:bg-cornflower hover:text-cream 
                ${
                  activePath === sub.path
                    ? "bg-marigold text-cream hover:bg-marigold cursor-auto"
                    : ""
                }`}
              onClick={() => navigate(sub.path)}
            >
              {sub.label}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

export default function SideNav() {
  const isSideNavOpen = useLayoutStore((state) => state.isSideNavOpen);
  const navigate = useNavigate();
  const location = useLocation();
  const paths = constants.ROUTE_PATHS;
  const [isSubmenuOpen, setIsSubmenuOpen] = useState({});

  const toggleSubmenu = (label) => {
    setIsSubmenuOpen((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const navItems = [
    { label: "Dashboard", path: paths.DASHBOARD, icon: HomeIcon },
    {
      label: "Self Assessment",
      icon: GoalIcon,
      subitems: [
        { label: "Recruitment", path: paths.SELF_ASSESSMENT_RECRUITMENT },
        { label: "Screening", path: paths.SELF_ASSESSMENT_SCREENING },
        { label: "Training", path: paths.SELF_ASSESSMENT_TRAINING },
        { label: "Scheduling", path: paths.SELF_ASSESSMENT_SCHEDULING },
        {
          label: "Data Collection",
          path: paths.SELF_ASSESSMENT_DATA_COLLECTION,
        },
        { label: "Recognition", path: paths.SELF_ASSESSMENT_RECOGNITION },
        { label: "Communication", path: paths.SELF_ASSESSMENT_COMMUNICATION },
        { label: "Data Security", path: paths.SELF_ASSESSMENT_DATA_SECURITY },
        { label: "Scorecard", path: paths.SELF_ASSESSMENT_SCORECARD },
      ],
    },
    {
      label: "Volunteer Engagement Strategy",
      icon: NetworkIcon,
      subitems: [
        {
          label: "Mission, Vision, Goals",
          path: paths.VOLUNTEER_ENGAGEMENT_MISSION_VISION_GOALS,
        },
        {
          label: "Role Design & Structure",
          path: paths.VOLUNTEER_ENGAGEMENT_ROLE_DESIGN_STRUCTURE,
        },
        { label: "Screening", path: paths.VOLUNTEER_ENGAGEMENT_SCREENING },
        {
          label: "Recruitment Strategy",
          path: paths.VOLUNTEER_ENGAGEMENT_RECRUITMENT_STRATEGY,
        },
        {
          label: "Onboarding & Training",
          path: paths.VOLUNTEER_ENGAGEMENT_ONBOARDING_TRAINING,
        },
        {
          label: "Engagement & Retention",
          path: paths.VOLUNTEER_ENGAGEMENT_ENGAGEMENT_RETENTION,
        },
        {
          label: "Measuring Impact",
          path: paths.VOLUNTEER_ENGAGEMENT_MEASURING_IMPACT,
        },
        {
          label: "Fundraising & Partnership",
          path: paths.VOLUNTEER_ENGAGEMENT_FUNDRAISING_PARTNERSHIP,
        },
        {
          label: "Technology & Automation",
          path: paths.VOLUNTEER_ENGAGEMENT_TECHNOLOGY_AUTOMATION,
        },
        {
          label: "Final Strategy Report",
          path: paths.VOLUNTEER_ENGAGEMENT_FINAL_STRATEGY_REPORT,
        },
      ],
    },
  ];

  return (
    <motion.div
      animate={{ x: isSideNavOpen ? 0 : "-100%" }}
      initial={{ x: "-100%" }}
      exit={{ x: "-100%" }}
      transition={{ type: "spring", stiffness: 95, damping: 20 }}
      style={{
        position: "fixed",
        top: "4rem",
        display: "flex",
        flexDirection: "column",
        width: "20rem",
        height: "calc(100vh - 4rem)",
        overflowY: "auto",
        borderRight: "2px solid #e5e5e5",
        alignItems: "center",
        overflowX: "hidden",
        zIndex: 50,
      }}
    >
      <nav className="flex w-full h-full px-4 justify-center bg-sky overflow-y-auto">
        <ul className="w-full">
          {_.map(navItems, (item) => (
            <NavItem
              key={item.label}
              item={item}
              navigate={navigate}
              activePath={location.pathname}
              toggleSubmenu={toggleSubmenu}
              isSubmenuOpen={isSubmenuOpen}
            />
          ))}
        </ul>
      </nav>
    </motion.div>
  );
}
